import React, { useEffect, useState } from "react";
import Select from "react-select";
import { useLocation, useNavigate } from "react-router-dom";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  faChevronCircleLeft,
  faFilter,
  faChevronDown,
  faChevronUp
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/* ================= COLUMN OPTIONS ================= */
const COLUMN_OPTIONS = [
  { value: "requestedQty", label: "Requested Qty" },
  { value: "issuedQty", label: "Issued Qty" },
  { value: "pendingToIssue", label: "Pending to Issue" },
  { value: "returnedQty", label: "Returned Qty" },
  { value: "pendingToReturn", label: "Pending to Return" }
];

const DepartmentIssueRequestReport = ({ accessedDepartments }) => {
  const location = useLocation();
  const navigate = useNavigate();

  /* ================= STATE ================= */
  const accessOfDepartments =
    location.state?.accessOfDepartments || accessedDepartments || "";

  const [departments, setDepartments] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null); // Issuer dept
  const [receivingDepts, setReceivingDepts] = useState([]); // 🔹 filter receiving depts

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);

  const [reportData, setReportData] = useState({});
  const [loading, setLoading] = useState(false);

  const [visibleColumns, setVisibleColumns] = useState(
    COLUMN_OPTIONS.map(c => c.value)
  );
  const [showColumnMenu, setShowColumnMenu] = useState(false);

  const [expandedRows, setExpandedRows] = useState({});

  const isVisible = col => visibleColumns.includes(col);

  /* ================= CLOSE COLUMN MENU ================= */
  useEffect(() => {
    const closeMenu = () => setShowColumnMenu(false);
    if (showColumnMenu) window.addEventListener("click", closeMenu);
    return () => window.removeEventListener("click", closeMenu);
  }, [showColumnMenu]);

  /* ================= DEPARTMENTS ================= */
  useEffect(() => {
    if (accessOfDepartments) {
      const list = accessOfDepartments.split(",").map(d => ({
        value: d.trim(),
        label: d.trim()
      }));
      setDepartments(list);
    }
  }, [accessOfDepartments]);

  /* ================= PROJECTS ================= */
  useEffect(() => {
    apiClient.get(`${SERVER_BASE}/area-projects`).then(res => {
      setProjects([
        { value: "Regular", label: "Regular" },
        ...res.data.map(p => ({
          value: p.id,
          label: p.projectName
        }))
      ]);
    });
  }, []);

  /* ================= GET REPORT ================= */
  const handleGet = async () => {
    if (!selectedDept || !selectedProject) {
      return Swal.fire("Warning", "Select all filters", "warning");
    }

    setLoading(true);
    try {
      const res = await apiClient.post(
        `${SERVER_BASE}/department-issue-request-report`,
        {
          department: selectedDept.value,
          projectId: selectedProject.value
        }
      );

      const grouped = {};

      res.data.forEach(req => {
        const dept = req.receivingDepartment || "Unknown";
        if (!grouped[dept]) grouped[dept] = [];

        req.items.forEach(item => {
          const issued = Number(item.totalIssuedQuantity || 0);
          const returned = Number(item.totalReturnedQuantity || 0);

          grouped[dept].push({
            type: req.type,
            requestNumber: req.requestNumber,

            itemName: item.itemName,
            uom: item.uom,

            requestedQuantity: Number(item.requestedQuantity || 0),
            issuedQuantity: issued,
            returnedQuantity: returned,

            pendingToIssue:
              Number(item.requestedQuantity || 0) - issued,

            pendingToReturn:
              issued - returned,

            issueSlips: item.issueSlips || [],
            returnSlips: item.returnSlips || []
          });
        });
      });

      setExpandedRows({});
      setReportData(grouped);

    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to fetch report", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ================= FILTER BY RECEIVING DEPARTMENTS ================= */
  const filteredReportData = Object.entries(reportData).filter(([dept]) => {
    if (!receivingDepts.length) return true;
    return receivingDepts.some(d => d.value === dept);
  });

  /* ================= TOGGLE ROW ================= */
  const toggleRow = (key) => {
    setExpandedRows(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  /* ================= UI ================= */
  return (
    <div className="formcontainer mt-4">
      <div className="d-flex align-items-center mb-3">
        {!accessedDepartments && (
          <button
            className="btn btn-secondary btn-sm me-3"
            onClick={() => navigate(-1)}
          >
            <FontAwesomeIcon icon={faChevronCircleLeft} /> Back
          </button>
        )}
        <h5 className="mb-0">Department Issue Report</h5>
      </div>

      {/* ================= FILTERS ================= */}
      <div className="row g-3 mb-4 align-items-end">

        {/* Issuer Department */}
        <div className="col-md-3">
          <label className="form-label fw-semibold">Issuer Department</label>
          <Select options={departments} value={selectedDept} onChange={setSelectedDept} />
        </div>

        {/* Receiving Departments Filter */}
        <div className="col-md-3">
          <label className="form-label fw-semibold">Receiving Departments</label>
          <Select
            options={departments}
            value={receivingDepts}
            onChange={setReceivingDepts}
            isMulti
          />
        </div>

        {/* Project */}
        <div className="col-md-3">
          <label className="form-label fw-semibold">Project</label>
          <Select options={projects} value={selectedProject} onChange={setSelectedProject} />
        </div>

        <div className="col-md-1">
          <button className="btn btn-primary w-100" onClick={handleGet}>
            {loading ? "..." : "Get"}
          </button>
        </div>

        {/* COLUMN FILTER */}
        <div className="col-md-1 position-relative">
          <button
            className="btn btn-outline-secondary btn-sm"
            title="Select Columns"
            onClick={e => {
              e.stopPropagation();
              setShowColumnMenu(prev => !prev);
            }}
          >
            <FontAwesomeIcon icon={faFilter} />
          </button>

          {showColumnMenu && (
            <div
              className="border rounded p-2 bg-white shadow position-absolute mt-2"
              style={{ zIndex: 1000, minWidth: "220px" }}
              onClick={e => e.stopPropagation()}
            >
              {COLUMN_OPTIONS.map(col => (
                <div className="form-check" key={col.value}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={visibleColumns.includes(col.value)}
                    onChange={() =>
                      setVisibleColumns(prev =>
                        prev.includes(col.value)
                          ? prev.filter(v => v !== col.value)
                          : [...prev, col.value]
                      )
                    }
                  />
                  <label className="form-check-label">
                    {col.label}
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ================= REPORT TABLE ================= */}
      {filteredReportData.map(([dept, items]) => (
        <div key={dept} className="mb-4">
          <h6 className="text-primary">Receiving Department: {dept}</h6>

          <div className="table-responsive">
            <table className="table table-bordered table-sm">
              <thead className="table-light">
                <tr>
                  <th></th>
                  <th>#</th>
                  <th>Type</th>
                  <th>Request No</th>
                  <th>Item</th>
                  {isVisible("requestedQty") && <th>Requested</th>}
                  {isVisible("issuedQty") && <th>Issued</th>}
                  {isVisible("pendingToIssue") && <th>Pending Issue</th>}
                  {isVisible("returnedQty") && <th>Returned</th>}
                  {isVisible("pendingToReturn") && <th>Pending Return</th>}
                  <th>UOM</th>
                </tr>
              </thead>

              <tbody>
                {items.map((row, i) => {
                  const rowKey = `${dept}-${i}`;
                  const expanded = expandedRows[rowKey];

                  return (
                    <React.Fragment key={rowKey}>
                      {/* MAIN ROW */}
                      <tr>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => toggleRow(rowKey)}
                          >
                            <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
                          </button>
                        </td>

                        <td>{i + 1}</td>
                        <td>{row.type}</td>
                        <td>{row.requestNumber}</td>
                        <td>{row.itemName}</td>

                        {isVisible("requestedQty") && <td>{row.requestedQuantity}</td>}
                        {isVisible("issuedQty") && <td>{row.issuedQuantity}</td>}
                        {isVisible("pendingToIssue") && <td>{row.pendingToIssue}</td>}
                        {isVisible("returnedQty") && <td>{row.returnedQuantity}</td>}
                        {isVisible("pendingToReturn") && <td>{row.pendingToReturn}</td>}
                        <td>{row.uom}</td>
                      </tr>

                      {/* EXPANDED DETAILS */}
                      {expanded && (
                        <tr>
                          <td colSpan={11}>
                            <div className="row g-3 mt-2">

                              {/* ISSUE SLIPS */}
                              <div className="col-md-6">
                                <div className="card border-primary">
                                  <div className="card-header bg-primary text-white fw-semibold">
                                    Issue Slips
                                  </div>
                                  <div className="card-body p-2">
                                    {row.issueSlips.length ? (
                                      <table className="table table-sm table-bordered mb-0">
                                        <thead>
                                          <tr>
                                            <th>Slip</th>
                                            <th>Qty</th>
                                            <th>Remarks</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {row.issueSlips.map((s, idx) => (
                                            <tr key={idx}>
                                              <td>{s.issueSlipCode}</td>
                                              <td>{s.issuedQuantity}</td>
                                              <td>{s.remarks || "-"}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    ) : <div className="text-muted small">No Issue Slips</div>}
                                  </div>
                                </div>
                              </div>

                              {/* RETURN SLIPS */}
                              <div className="col-md-6">
                                <div className="card border-success">
                                  <div className="card-header bg-success text-white fw-semibold">
                                    Return Slips (Accepted)
                                  </div>
                                  <div className="card-body p-2">
                                    {row.returnSlips.length ? (
                                      <table className="table table-sm table-bordered mb-0">
                                        <thead>
                                          <tr>
                                            <th>Slip</th>
                                            <th>Qty</th>
                                            <th>Remarks</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {row.returnSlips.map((s, idx) => (
                                            <tr key={idx}>
                                              <td>{s.returnSlipCode}</td>
                                              <td>{s.returnedQuantity}</td>
                                              <td>{s.remarks || "-"}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    ) : <div className="text-muted small">No Accepted Returns</div>}
                                  </div>
                                </div>
                              </div>

                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default DepartmentIssueRequestReport;
